/* Teacher / Music Instructor calendar — Day / Week / Month views with
   side panels for replacements, reschedules, temp-stop and "offer more time".
   Read-only data; teachers see only their own classes. Admin can pass
   ?teacher_id=<id> to view any teacher's calendar. */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { errMsg, DAYS } from "@/lib/api";
import { PageHeader, Spinner, StatCard, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CalendarDays, Sun, Clock, ChevronLeft, ChevronRight, Calendar as CalIcon,
  Users, RefreshCcw, LifeBuoy, PauseCircle, Gauge, GraduationCap, Sparkles,
  PartyPopper, AlertTriangle, ArrowRight, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { LessonStatusBadge, LessonDetailDrawer } from "@/components/calendar/LessonStatusBadge";

const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Visual style per event kind (lines tuned for the music-school palette)
const EVENT_STYLE = {
  class:       { cls: "bg-emerald-100 border-emerald-300 text-emerald-900", dot: "bg-emerald-500", label: "Class" },
  new_class:   { cls: "bg-violet-100 border-violet-300 text-violet-900", dot: "bg-violet-500", label: "New Student" },
  trial:       { cls: "bg-amber-100 border-amber-300 text-amber-900", dot: "bg-amber-500", label: "Trial" },
  replacement: { cls: "bg-orange-100 border-orange-300 text-orange-900", dot: "bg-orange-500", label: "Replacement" },
  reschedule:  { cls: "bg-teal-100 border-teal-300 text-teal-900", dot: "bg-teal-500", label: "Rescheduled" },
  temp_stop:   { cls: "bg-zinc-100 border-zinc-300 border-dashed text-zinc-700", dot: "bg-zinc-400", label: "Temp Stop" },
  reserved:    { cls: "bg-zinc-100 border-zinc-300 border-dashed text-zinc-700", dot: "bg-zinc-400", label: "Reserved" },
  cancelled:   { cls: "bg-zinc-100 border-zinc-200 text-zinc-500 line-through", dot: "bg-zinc-400", label: "Cancelled" },
  booking:     { cls: "bg-blue-100 border-blue-300 text-blue-900", dot: "bg-blue-500", label: "Booking" },
};

// Overrides driven by resolve_lesson_status — take precedence over `kind`
// so a class that was reshuffled reads correctly at a glance in the week grid.
const LESSON_STATUS_STYLE = {
  no_class_teacher_leave: { cls: "bg-zinc-200 border-red-400 text-zinc-500 line-through decoration-2", dot: "bg-red-500", label: "TEACHER LEAVE", badge: "LEAVE" },
  no_class_teacher_mc:    { cls: "bg-rose-100 border-rose-500 text-rose-900 line-through decoration-2", dot: "bg-rose-500", label: "TEACHER MC", badge: "MC" },
  no_class_teacher_el:    { cls: "bg-red-100 border-red-600 text-red-900 line-through decoration-2", dot: "bg-red-600", label: "EMERGENCY LEAVE", badge: "EL" },
  no_class_student_leave: { cls: "bg-slate-200 border-slate-400 text-slate-500 line-through decoration-2", dot: "bg-slate-500", label: "STUDENT LEAVE", badge: "STU LEAVE" },
  replacement_pending:    { cls: "bg-amber-100 border-amber-500 text-amber-900 border-dashed", dot: "bg-amber-500", label: "REPLACEMENT PENDING", badge: "PENDING" },
  replacement_confirmed:  { cls: "bg-teal-100 border-teal-500 text-teal-900 border-dashed line-through", dot: "bg-teal-500", label: "REPLACED", badge: "→ REPL" },
  fully_replaced:         { cls: "bg-lime-100 border-lime-500 text-lime-900 line-through", dot: "bg-lime-500", label: "FULLY REPLACED", badge: "REPLACED" },
  partially_replaced:     { cls: "bg-yellow-100 border-yellow-500 text-yellow-900", dot: "bg-yellow-600", label: "PARTIALLY REPL", badge: "PART" },
  group_affected:         { cls: "bg-orange-100 border-orange-600 text-orange-900", dot: "bg-orange-500", label: "GROUP AFFECTED", badge: "GROUP" },
  cancelled:              { cls: "bg-zinc-100 border-zinc-400 text-zinc-500 line-through decoration-2", dot: "bg-zinc-400", label: "Cancelled", badge: "CANX" },
};

function styleFor(ev) {
  // lesson_status (resolved by backend) has the highest priority. It correctly
  // reflects leave-overlays and replacement chains — the raw `kind` alone
  // would still show a leave-affected class as a normal green block.
  if (ev.lesson_status && LESSON_STATUS_STYLE[ev.lesson_status]) return LESSON_STATUS_STYLE[ev.lesson_status];
  if (ev.status === "cancelled") return EVENT_STYLE.cancelled;
  if (ev.kind === "class" && ev.is_new_student) return EVENT_STYLE.new_class;
  return EVENT_STYLE[ev.kind] || EVENT_STYLE.class;
}

// Short human-readable "→ 12 Jul 10:00" pointer for the replaced-class badge.
function replPointer(ev) {
  if (!ev.replacement_date) return null;
  const d = new Date(ev.replacement_date + "T00:00:00");
  const day = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return `→ ${day}${ev.replacement_start_time ? ` ${ev.replacement_start_time}` : ""}`;
}

const TIME_FROM = 9;  // 9 AM
const TIME_TO = 22;   // 10 PM
const HOUR_PX = 56;
const SLOT_PX = HOUR_PX / 4;  // 15-min granularity

function minutesFromTop(t) {
  if (!t) return 0;
  const [h, m] = t.split(":").map(Number);
  return ((h - TIME_FROM) * 60 + m);
}
function eventTop(t) { return (minutesFromTop(t) / 60) * HOUR_PX; }
function eventHeight(s, e) {
  const start = minutesFromTop(s), end = minutesFromTop(e);
  return Math.max(((end - start) / 60) * HOUR_PX, 24);
}

function isNow(dateStr, startTime, endTime) {
  const now = new Date();
  const today = fmtDate(now);
  if (dateStr !== today) return false;
  const cur = now.getHours() * 60 + now.getMinutes();
  return minutesFromTop(startTime) <= cur && cur < minutesFromTop(endTime);
}

function fmtDate(d) {
  // Use LOCAL date parts (not toISOString, which converts to UTC and shifts
  // by one day for UTC+8 Malaysia — bucketing a Fri lesson into the Sat column).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function addDays(d, n) {
  const out = new Date(d); out.setDate(out.getDate() + n); return out;
}
function startOfWeek(d) {
  const out = new Date(d);
  const dow = (out.getDay() + 6) % 7;  // Monday = 0
  out.setDate(out.getDate() - dow);
  return out;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

// ---------- Legend chip ----------
function LegendChip({ kind, label }) {
  const s = EVENT_STYLE[kind] || EVENT_STYLE.class;
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      <span className={`inline-block w-3 h-3 rounded ${s.cls.split(" ")[0]} ${s.cls.split(" ")[1]}`} />
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

// ---------- Day view (vertical timeline) ----------
function DayView({ events, anchor, holidayByDate, availability, onLessonClick }) {
  const dayStr = fmtDate(anchor);
  const holiday = holidayByDate[dayStr];
  const dayEvents = events.filter((e) => e.date === dayStr)
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const now = new Date();
  const isToday = dayStr === fmtDate(now);
  const nowPx = isToday ? eventTop(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`) : null;
  // Availability outline blocks for today's day-of-week
  const dow = (anchor.getDay() + 6) % 7;
  const dayAvail = availability.filter((w) => w.day_of_week === dow);

  return (
    <div className="rounded-lg border bg-card" data-testid="day-view">
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <p className="text-sm font-semibold">
            {anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
          <p className="text-xs text-muted-foreground">{dayEvents.length} item{dayEvents.length === 1 ? "" : "s"}</p>
        </div>
        {holiday && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-100 border border-rose-200 px-3 py-1 rounded-full"
                data-testid="day-holiday-banner">
            <PartyPopper className="h-3.5 w-3.5" />{holiday.label}
          </span>
        )}
      </div>
      <div className="relative" style={{ height: (TIME_TO - TIME_FROM) * HOUR_PX }}>
        {/* Hour lines */}
        {Array.from({ length: TIME_TO - TIME_FROM + 1 }, (_, i) => TIME_FROM + i).map((h) => (
          <div key={h}
               className="absolute left-0 right-0 border-t border-zinc-100 text-[10px] text-muted-foreground pl-2"
               style={{ top: (h - TIME_FROM) * HOUR_PX }}>
            {String(h).padStart(2, "0")}:00
          </div>
        ))}
        {/* Holiday shading */}
        {holiday && (
          <div className="absolute inset-0 bg-rose-50/40 pointer-events-none" />
        )}
        {/* Availability outlines */}
        {!holiday && dayAvail.map((w, i) => (
          <div key={i}
               className="absolute right-2 left-16 border-2 border-dashed border-emerald-300/60 rounded-md bg-emerald-50/20 pointer-events-none"
               style={{ top: eventTop(w.start_time), height: eventHeight(w.start_time, w.end_time) }}
               title={`Available — ${w.start_time}–${w.end_time}`} />
        ))}
        {/* Event blocks */}
        {dayEvents.map((ev) => {
          const s = styleFor(ev);
          const live = isNow(ev.date, ev.start_time, ev.end_time);
          const clickable = ev.class_schedule_id || ev.source_id;
          const overrideStatus = ev.lesson_status && ev.lesson_status !== "scheduled";
          const isLeave = ev.lesson_status?.startsWith("no_class_teacher");
          const isReplaced = ev.lesson_status === "replacement_confirmed"
                             || ev.lesson_status === "fully_replaced"
                             || ev.lesson_status === "partially_replaced";
          const pointer = replPointer(ev);
          return (
            <div key={ev.id}
                 onClick={clickable ? () => onLessonClick?.(ev) : undefined}
                 className={`absolute right-3 left-20 rounded-lg border-2 px-3 py-2 ${s.cls} ${live ? "ring-2 ring-emerald-500 shadow-md" : "shadow-sm"} ${clickable ? "cursor-pointer hover:brightness-95" : ""}`}
                 style={{ top: eventTop(ev.start_time), height: eventHeight(ev.start_time, ev.end_time) }}
                 data-testid={`day-event-${ev.id}`}
                 title={`${ev.student_name || ""} · ${ev.course_name || ""}${pointer ? ` · ${pointer}` : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-mono">{ev.start_time}–{ev.end_time}</p>
                  <p className="text-sm font-semibold truncate">{ev.student_name || "—"}</p>
                  <p className="text-xs truncate opacity-80">
                    {ev.course_name}{ev.studio_name ? ` · ${ev.studio_name}` : ""}
                  </p>
                  {overrideStatus && (
                    <div className="mt-1">
                      <LessonStatusBadge status={ev.lesson_status} />
                    </div>
                  )}
                  {pointer && (
                    <p className="text-xs font-bold text-primary mt-1"
                       data-testid={`day-event-${ev.id}-pointer`}>
                      Replaced {pointer}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {(isLeave || isReplaced) && s.badge && (
                    <span className="text-[9px] font-black uppercase bg-red-600 text-white rounded px-1.5 py-0.5 shadow"
                          data-testid={`day-event-${ev.id}-badge`}>
                      {s.badge}
                    </span>
                  )}
                  <span className="text-[9px] uppercase font-bold tracking-wider">{s.label}</span>
                  {live && <span className="text-[9px] uppercase font-bold text-emerald-700 animate-pulse">● LIVE</span>}
                </div>
              </div>
            </div>
          );
        })}
        {/* "Now" line */}
        {nowPx !== null && (
          <div className="absolute left-0 right-0 border-t-2 border-rose-500 pointer-events-none"
               style={{ top: nowPx }}
               data-testid="now-line">
            <span className="absolute -top-2 left-2 bg-rose-500 text-white text-[9px] font-bold px-1.5 rounded">
              NOW
            </span>
          </div>
        )}
      </div>
      {dayEvents.length === 0 && !holiday && (
        <div className="p-8"><EmptyState text="No classes today. Enjoy a quiet day or update your availability." /></div>
      )}
    </div>
  );
}

// ---------- Week view (main grid) ----------
function WeekView({ events, weekStart, holidayByDate, availability, onLessonClick }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const eventsByDay = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const arr = m.get(e.date) || [];
      arr.push(e); m.set(e.date, arr);
    });
    return m;
  }, [events]);

  const todayStr = fmtDate(new Date());
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();

  return (
    <div className="rounded-lg border bg-card overflow-x-auto" data-testid="week-view">
      <div className="min-w-[860px]">
        {/* Header row */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b">
          <div className="p-2 text-[10px] uppercase tracking-wider text-muted-foreground">Time</div>
          {days.map((d) => {
            const dStr = fmtDate(d);
            const isToday = dStr === todayStr;
            const holiday = holidayByDate[dStr];
            return (
              <div key={dStr}
                   className={`p-2 text-center border-l ${isToday ? "bg-emerald-50/40" : ""}`}
                   data-testid={`week-day-header-${dStr}`}>
                <p className={`text-[10px] uppercase tracking-wider ${isToday ? "text-emerald-700 font-bold" : "text-muted-foreground"}`}>
                  {DAYS_SHORT[(d.getDay() + 6) % 7]}
                </p>
                <p className={`text-base font-bold ${isToday ? "text-emerald-700" : ""}`}>
                  {d.getDate()}
                </p>
                {holiday && (
                  <p className="text-[9px] font-bold text-rose-700 mt-0.5 truncate" title={holiday.label}>
                    {holiday.holiday_type === "yamaha_school_holiday" ? "Yamaha" : "Holiday"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {/* Body */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)]"
             style={{ height: (TIME_TO - TIME_FROM) * HOUR_PX }}>
          {/* Time column */}
          <div className="relative border-r">
            {Array.from({ length: TIME_TO - TIME_FROM + 1 }, (_, i) => TIME_FROM + i).map((h) => (
              <div key={h} className="absolute left-0 right-0 text-[10px] text-muted-foreground pl-2 border-t border-zinc-100"
                   style={{ top: (h - TIME_FROM) * HOUR_PX }}>
                {String(h).padStart(2, "0")}
              </div>
            ))}
          </div>
          {/* Day columns */}
          {days.map((d) => {
            const dStr = fmtDate(d);
            const isToday = dStr === todayStr;
            const dayEvents = (eventsByDay.get(dStr) || [])
              .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
            const dow = (d.getDay() + 6) % 7;
            const dayAvail = availability.filter((w) => w.day_of_week === dow);
            const holiday = holidayByDate[dStr];
            return (
              <div key={dStr} className={`relative border-l ${isToday ? "bg-emerald-50/20" : ""}`}
                   data-testid={`week-day-${dStr}`}>
                {/* Hour grid lines */}
                {Array.from({ length: TIME_TO - TIME_FROM + 1 }, (_, i) => i).map((i) => (
                  <div key={i} className="absolute left-0 right-0 border-t border-zinc-100"
                       style={{ top: i * HOUR_PX }} />
                ))}
                {holiday && (
                  <div className="absolute inset-0 bg-rose-50/40 pointer-events-none"
                       title={holiday.label}>
                    <span className="absolute top-1 left-1 right-1 text-[9px] font-bold text-rose-700 text-center">
                      {holiday.label}
                    </span>
                  </div>
                )}
                {/* Availability outlines */}
                {!holiday && dayAvail.map((w, i) => (
                  <div key={i}
                       className="absolute left-1 right-1 border-2 border-dashed border-emerald-300/60 rounded bg-emerald-50/20 pointer-events-none"
                       style={{ top: eventTop(w.start_time), height: eventHeight(w.start_time, w.end_time) }}
                       title={`Available · ${w.start_time}–${w.end_time}`} />
                ))}
                {/* Event blocks */}
                {dayEvents.map((ev) => {
                  const s = styleFor(ev);
                  const live = isNow(ev.date, ev.start_time, ev.end_time);
                  const clickable = ev.class_schedule_id || ev.source_id;
                  const isLeave = ev.lesson_status?.startsWith("no_class_teacher");
                  const isReplaced = ev.lesson_status === "replacement_confirmed"
                                     || ev.lesson_status === "fully_replaced"
                                     || ev.lesson_status === "partially_replaced";
                  const pointer = replPointer(ev);
                  return (
                    <div key={ev.id}
                         onClick={clickable ? () => onLessonClick?.(ev) : undefined}
                         className={`absolute left-1 right-1 rounded border-2 px-1.5 py-1 text-[10px] leading-tight ${s.cls} ${live ? "ring-2 ring-emerald-500 shadow-md z-10" : ""} ${clickable ? "cursor-pointer hover:brightness-95" : ""}`}
                         style={{ top: eventTop(ev.start_time), height: eventHeight(ev.start_time, ev.end_time) }}
                         data-testid={`week-event-${ev.id}`}
                         data-lesson-status={ev.lesson_status || ""}
                         title={`${ev.start_time}–${ev.end_time} · ${ev.student_name || ""} · ${s.label}${pointer ? ` ${pointer}` : ""}`}>
                      {(isLeave || isReplaced) && s.badge && (
                        <span className="absolute -top-1 -right-1 text-[8px] font-black uppercase bg-red-600 text-white rounded px-1 shadow"
                              data-testid={`week-event-${ev.id}-badge`}>
                          {s.badge}
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        <p className="font-mono text-[9px] opacity-80">{ev.start_time}</p>
                      </div>
                      <p className="font-semibold truncate">{ev.student_name || "—"}</p>
                      <p className="opacity-80 truncate">{ev.course_name || ""}</p>
                      {pointer && (
                        <p className="text-[9px] font-bold text-primary truncate mt-0.5"
                           data-testid={`week-event-${ev.id}-pointer`}>
                          {pointer}
                        </p>
                      )}
                      {live && <span className="text-[8px] uppercase font-bold text-emerald-700">● LIVE</span>}
                    </div>
                  );
                })}
                {/* Now line on today */}
                {isToday && nowMin >= TIME_FROM * 60 && nowMin < TIME_TO * 60 && (
                  <div className="absolute left-0 right-0 border-t-2 border-rose-500 z-20 pointer-events-none"
                       style={{ top: ((nowMin - TIME_FROM * 60) / 60) * HOUR_PX }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Month view ----------
function MonthView({ events, anchor, holidayByDate, onDayClick }) {
  const first = startOfMonth(anchor);
  const last = endOfMonth(anchor);
  // Start grid from Monday of first week
  const gridStart = startOfWeek(first);
  const days = [];
  let cur = new Date(gridStart);
  while (cur <= last || days.length % 7 !== 0) {
    days.push(new Date(cur));
    cur = addDays(cur, 1);
    if (days.length >= 42) break;
  }
  const todayStr = fmtDate(new Date());
  // Counts by day
  const countsByDay = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const c = m.get(e.date) || { total: 0, trial: 0, replacement: 0 };
      c.total += 1;
      if (e.kind === "trial") c.trial += 1;
      if (e.kind === "replacement") c.replacement += 1;
      m.set(e.date, c);
    });
    return m;
  }, [events]);

  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid="month-view">
      <div className="grid grid-cols-7 border-b">
        {DAYS_SHORT.map((d) => (
          <div key={d} className="p-2 text-center text-[10px] uppercase tracking-wider text-muted-foreground border-l first:border-l-0">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const dStr = fmtDate(d);
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = dStr === todayStr;
          const holiday = holidayByDate[dStr];
          const c = countsByDay.get(dStr);
          return (
            <button key={i}
                    onClick={() => onDayClick(d)}
                    className={`text-left min-h-[80px] p-1.5 border-l border-t first:border-l-0 transition-colors
                                ${!inMonth ? "bg-muted/30 text-muted-foreground" : "hover:bg-emerald-50/30"}
                                ${isToday ? "ring-2 ring-emerald-500 ring-inset" : ""}
                                ${holiday ? "bg-rose-50/40" : ""}`}
                    data-testid={`month-day-${dStr}`}>
              <p className={`text-xs font-bold ${isToday ? "text-emerald-700" : ""}`}>
                {d.getDate()}
              </p>
              {holiday && (
                <p className="text-[9px] font-bold text-rose-700 truncate mt-0.5" title={holiday.label}>
                  {holiday.label}
                </p>
              )}
              {c && c.total > 0 && (
                <div className="mt-1 space-y-0.5">
                  <p className="text-[10px] text-emerald-800">
                    <span className="font-bold">{c.total}</span> class{c.total === 1 ? "" : "es"}
                  </p>
                  {c.trial > 0 && <p className="text-[9px] text-amber-700">{c.trial} trial</p>}
                  {c.replacement > 0 && <p className="text-[9px] text-orange-700">{c.replacement} repl.</p>}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Side panel cards (slimmer versions for the calendar layout) ----------
function PanelCard({ icon: Icon, title, count, color = "amber", children, testId }) {
  const bg = { amber: "amber", blue: "blue", rose: "rose" }[color] || "amber";
  return (
    <div className="rounded-lg border bg-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-secondary" />
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-${bg}-100 text-${bg}-800 border border-${bg}-200`}>
          {count}
        </span>
      </div>
      {children}
    </div>
  );
}

function OfferMoreTimePrompt({ pct }) {
  if ((pct ?? 0) < 75) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-4" data-testid="offer-prompt">
      <div className="flex items-start gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-900">
          Your teaching availability is highly utilised ({pct}%). Consider offering more teaching time
          so admin can arrange new students or trial classes.
        </p>
      </div>
      <Link to="/teacher/availability">
        <Button size="sm" className="w-full" data-testid="offer-more-time-cta">
          <Plus className="h-3.5 w-3.5 mr-1" />Offer more teaching time
        </Button>
      </Link>
    </div>
  );
}

// ---------- Main page ----------
export default function TeacherCalendarPage({ adminMode = false, teacherIdOverride = null }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("view") || "week");
  const [anchor, setAnchor] = useState(() => {
    const p = searchParams.get("date");
    return p ? parseDate(p) : new Date();
  });
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // P3: lesson detail drawer
  const [drawerEvent, setDrawerEvent] = useState(null); // {class_schedule_id, date}
  const openLessonDrawer = (ev) => {
    const csid = ev.class_schedule_id || ev.source_id;
    if (!csid || ev.kind !== "class") return;
    setDrawerEvent({ class_schedule_id: csid, date: ev.date });
  };

  // Compute backend range based on view + anchor
  const range = useMemo(() => {
    if (view === "day") return [fmtDate(anchor), fmtDate(anchor)];
    if (view === "week") {
      const ws = startOfWeek(anchor);
      return [fmtDate(ws), fmtDate(addDays(ws, 6))];
    }
    // month — pad to full grid weeks
    const s = startOfWeek(startOfMonth(anchor));
    const e = addDays(s, 41);
    return [fmtDate(s), fmtDate(e)];
  }, [view, anchor]);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = adminMode && teacherIdOverride
        ? `/admin/teachers/${teacherIdOverride}/calendar`
        : "/teacher/calendar";
      const r = await api.get(endpoint, { params: { from: range[0], to: range[1] } });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  }, [range, adminMode, teacherIdOverride]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  // Sync URL params
  useEffect(() => {
    setSearchParams({ view, date: fmtDate(anchor) }, { replace: true });
  }, [view, anchor, setSearchParams]);

  const navigate = (delta) => {
    if (view === "day") setAnchor(addDays(anchor, delta));
    else if (view === "week") setAnchor(addDays(anchor, 7 * delta));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1));
  };

  if (loading || !data) return <Spinner full />;

  const holidayByDate = (data.holidays || []).reduce((acc, h) => {
    acc[h.date] = h; return acc;
  }, {});

  // Filter events by class type
  const filtered = data.events.filter((e) => {
    if (filter === "all") return true;
    if (filter === "class") return e.kind === "class" && !e.is_new_student;
    if (filter === "new") return e.is_new_student;
    if (filter === "trial") return e.kind === "trial";
    if (filter === "replacement") return e.kind === "replacement";
    if (filter === "temp_stop") return e.kind === "temp_stop" || e.kind === "reserved";
    return true;
  });

  // Summary card metrics
  const todayStr = fmtDate(new Date());
  const todaysCnt = data.events.filter((e) => e.date === todayStr).length;
  const weekStart = startOfWeek(new Date());
  const weekEnd = addDays(weekStart, 6);
  const weekS = fmtDate(weekStart), weekE = fmtDate(weekEnd);
  const thisWeekCnt = data.events.filter((e) => e.date >= weekS && e.date <= weekE).length;
  const trialThisWeekCnt = data.events.filter(
    (e) => e.kind === "trial" && e.date >= weekS && e.date <= weekE).length;
  const monthHolidayCnt = (data.holidays || []).filter(
    (h) => h.date.slice(0, 7) === todayStr.slice(0, 7)).length;

  return (
    <div data-testid="teacher-calendar-page">
      <PageHeader
        title={adminMode ? `Calendar · ${data.teacher.full_name}` : "My calendar"}
        description="Day, week and month views of your teaching schedule."
        actions={
          <div className="inline-flex rounded-md border bg-muted/30 p-1 text-xs" data-testid="view-toggle">
            {["day", "week", "month"].map((v) => (
              <button key={v} onClick={() => setView(v)}
                      data-testid={`view-${v}`}
                      className={`px-3 py-1.5 rounded transition-colors capitalize ${view === v
                        ? "bg-card text-foreground shadow-sm border" : "text-muted-foreground hover:text-foreground"}`}>
                {v}
              </button>
            ))}
          </div>
        } />

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6 stagger-in">
        <StatCard icon={Sun} label="Today" value={todaysCnt} accent testId="sum-today" />
        <StatCard icon={CalendarDays} label="This week" value={thisWeekCnt} testId="sum-week" />
        <StatCard icon={Sparkles} label="Trials this wk" value={trialThisWeekCnt} testId="sum-trials" />
        <StatCard icon={LifeBuoy} label="Replacements" value={data.pending_replacements.length} testId="sum-repls" />
        <StatCard icon={PartyPopper} label="Holidays / month" value={monthHolidayCnt} testId="sum-holidays" />
        <StatCard icon={Gauge} label="Utilisation" value={`${data.utilisation?.utilisation_pct ?? 0}%`} testId="sum-util" />
      </div>

      {/* Controls bar */}
      <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
        <div className="flex flex-col gap-2">
          <div>
            <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="nav-today">
              <CalIcon className="h-4 w-4 mr-1" />Today
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="nav-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input type="date" value={fmtDate(anchor)} onChange={(e) => setAnchor(parseDate(e.target.value))}
                   className="w-44" data-testid="date-picker" />
            <Button variant="outline" size="sm" onClick={() => navigate(1)} data-testid="nav-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold ml-3" data-testid="current-label">
              {view === "month"
                ? anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
                : view === "week"
                ? `${fmtDate(startOfWeek(anchor))} → ${fmtDate(addDays(startOfWeek(anchor), 6))}`
                : anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
                  data-testid="filter-kind"
                  className="border rounded-md px-3 py-1.5 text-xs bg-card">
            <option value="all">All classes</option>
            <option value="class">Existing students</option>
            <option value="new">New students</option>
            <option value="trial">Trial only</option>
            <option value="replacement">Replacement only</option>
            <option value="temp_stop">Temp stop / reserved</option>
          </select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 px-3 py-2 rounded-md border bg-muted/20" data-testid="legend">
        <LegendChip kind="class" label="Existing student" />
        <LegendChip kind="new_class" label="New student" />
        <LegendChip kind="trial" label="Trial" />
        <LegendChip kind="replacement" label="Replacement" />
        <LegendChip kind="reschedule" label="Rescheduled" />
        <LegendChip kind="temp_stop" label="Temp stop / reserved" />
        <LegendChip kind="cancelled" label="Cancelled" />
        <div className="flex items-center gap-1.5 text-[11px]" data-testid="legend-teacher-leave">
          <span className="inline-block w-3 h-3 rounded bg-zinc-200 border-2 border-red-400" />
          <span className="text-muted-foreground">Teacher on leave / MC / EL</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]" data-testid="legend-replaced">
          <span className="inline-block w-3 h-3 rounded bg-teal-100 border-2 border-teal-500 border-dashed" />
          <span className="text-muted-foreground">Replaced (see pointer)</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="inline-block w-3 h-3 rounded border-2 border-dashed border-emerald-300 bg-emerald-50/20" />
          <span className="text-muted-foreground">Available (outline)</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="inline-block w-3 h-3 rounded bg-rose-100 border border-rose-200" />
          <span className="text-muted-foreground">Yamaha school holiday</span>
        </div>
      </div>

      {/* Main grid: calendar + side panels */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3" data-testid="calendar-main">
          {view === "day" && (
            <DayView events={filtered} anchor={anchor} holidayByDate={holidayByDate}
                     availability={data.availability} onLessonClick={openLessonDrawer} />)}
          {view === "week" && (
            <WeekView events={filtered} weekStart={startOfWeek(anchor)} holidayByDate={holidayByDate}
                      availability={data.availability} onLessonClick={openLessonDrawer} />)}
          {view === "month" && (
            <MonthView events={filtered} anchor={anchor} holidayByDate={holidayByDate}
                       onDayClick={(d) => { setAnchor(d); setView("day"); }} />)}

          {/* Mobile-only agenda list — only when on phone (we still show grid on md+) */}
          <div className="lg:hidden mt-4" data-testid="mobile-agenda">
            <h3 className="text-sm font-semibold mb-2">Agenda</h3>
            <div className="space-y-2">
              {filtered.slice(0, 30).map((ev) => {
                const s = styleFor(ev);
                return (
                  <div key={ev.id} className={`rounded-md border px-3 py-2 text-xs ${s.cls}`}>
                    <p className="font-mono text-[10px]">{ev.date} · {ev.start_time}–{ev.end_time}</p>
                    <p className="font-semibold">{ev.student_name || "—"}</p>
                    <p className="opacity-80">{ev.course_name} {ev.studio_name && `· ${ev.studio_name}`}</p>
                    <p className="text-[9px] uppercase font-bold mt-0.5">{s.label}</p>
                  </div>
                );
              })}
              {filtered.length === 0 && <EmptyState text="No items in this range." />}
            </div>
          </div>
        </div>

        {/* Side panels */}
        <div className="space-y-3" data-testid="side-panels">
          <OfferMoreTimePrompt pct={data.utilisation?.utilisation_pct} />

          <PanelCard icon={LifeBuoy} title="Replacements yet to teach"
                     count={data.pending_replacements.length} color="amber"
                     testId="panel-replacements">
            {data.pending_replacements.length === 0 ? (
              <p className="text-xs text-muted-foreground">All caught up.</p>
            ) : data.pending_replacements.slice(0, 5).map((r) => (
              <div key={r.id} className="text-xs border-t first:border-t-0 pt-2 mt-2 first:pt-0 first:mt-0">
                <p className="font-medium">{r.student_name}</p>
                <p className="text-muted-foreground">
                  Missed {r.original_date} → make-up <span className="font-mono">{r.replacement_date} {r.start_time}</span>
                </p>
              </div>
            ))}
          </PanelCard>

          <PanelCard icon={RefreshCcw} title="Classes being rescheduled"
                     count={data.being_rescheduled.length} color="blue"
                     testId="panel-rescheduling">
            {data.being_rescheduled.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nothing in discussion.</p>
            ) : data.being_rescheduled.slice(0, 5).map((r) => (
              <Link key={r.id} to={`/teacher/reschedule/${r.id}`}
                    className="block text-xs border-t first:border-t-0 pt-2 mt-2 first:pt-0 first:mt-0 hover:bg-muted/30 -mx-1 px-1 rounded">
                <p className="font-medium">{r.student_name}</p>
                <p className="text-muted-foreground">
                  <span className="font-mono">{r.original_date} {r.original_start_time}</span>
                  {r.proposed_date && (<> → <span className="font-mono">{r.proposed_date} {r.proposed_start_time || ""}</span></>)}
                </p>
                <p className="text-[10px] text-muted-foreground">Status: {r.status}</p>
              </Link>
            ))}
          </PanelCard>

          {data.temp_stop.length > 0 && (
            <PanelCard icon={PauseCircle} title="Temp stop / reserved"
                       count={data.temp_stop.length} color="rose"
                       testId="panel-temp-stop">
              {data.temp_stop.slice(0, 5).map((c) => (
                <div key={c.id} className="text-xs border-t first:border-t-0 pt-2 mt-2 first:pt-0 first:mt-0">
                  <p className="font-medium">{c.student_name}</p>
                  <p className="text-muted-foreground">
                    {c.day_of_week !== null && c.day_of_week !== undefined ? `${DAYS[c.day_of_week]} ` : ""}
                    {c.start_time && `${c.start_time}–${c.end_time}`}
                  </p>
                </div>
              ))}
            </PanelCard>
          )}

          {/* Holidays panel */}
          {(data.holidays || []).length > 0 && (
            <PanelCard icon={PartyPopper} title="Holidays in view"
                       count={data.holidays.length} color="rose"
                       testId="panel-holidays">
              {data.holidays.slice(0, 8).map((h) => (
                <div key={h.id} className="text-xs border-t first:border-t-0 pt-2 mt-2 first:pt-0 first:mt-0">
                  <p className="font-medium">{h.label}</p>
                  <p className="text-muted-foreground font-mono text-[10px]">{h.date}</p>
                </div>
              ))}
            </PanelCard>
          )}
        </div>
      </div>
      <LessonDetailDrawer
        classScheduleId={drawerEvent?.class_schedule_id}
        date={drawerEvent?.date}
        open={!!drawerEvent}
        onOpenChange={(o) => !o && setDrawerEvent(null)}
        role="teacher"
      />
    </div>
  );
}
